import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/core/components/ui/dialog';
import { Button } from '@/core/components/ui/button';
import { Input } from '@/core/components/ui/input';
import { Label } from '@/core/components/ui/label';
import { MAX_FLOW_NAME_LENGTH } from '../../types/kiosk-flow-types';

interface FlowNameDialogProps {
  open: boolean;
  mode: 'create' | 'rename';
  operatorName: string;
  // Prefilled name in rename mode
  initialName?: string;
  isSaving: boolean;
  error: string | null;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
}

// Name dialog shared by Create New Flow and Rename
export function FlowNameDialog({
  open,
  mode,
  operatorName,
  initialName = '',
  isSaving,
  error,
  onOpenChange,
  onSubmit,
}: FlowNameDialogProps) {
  const [name, setName] = useState('');

  // Reset to the initial name every time the dialog opens
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const trimmed = name.trim();
  const tooLong = trimmed.length > MAX_FLOW_NAME_LENGTH;
  const unchanged = mode === 'rename' && trimmed === initialName.trim();
  const canSubmit = trimmed !== '' && !tooLong && !unchanged && !isSaving;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (canSubmit) onSubmit(name);
  };

  const isRename = mode === 'rename';

  return (
    <Dialog open={open} onOpenChange={(next) => !isSaving && onOpenChange(next)}>
      <DialogContent className="sm:max-w-md">
        <form onSubmit={handleSubmit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{isRename ? 'Rename Flow' : 'Create New Flow'}</DialogTitle>
            <DialogDescription>
              {isRename ? `Kiosk flow for ${operatorName}` : `New kiosk flow for ${operatorName}`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label htmlFor="kiosk-flow-name">
              Flow name <span className="text-red-500">*</span>
            </Label>
            <Input
              id="kiosk-flow-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Default kiosk flow"
              disabled={isSaving}
              autoFocus
            />
            <div className="flex justify-between gap-2 text-xs">
              <span className="text-red-600">
                {tooLong ? `Flow name must be at most ${MAX_FLOW_NAME_LENGTH} characters` : ''}
              </span>
              <span className={tooLong ? 'text-red-600' : 'text-gray-400'}>
                {trimmed.length}/{MAX_FLOW_NAME_LENGTH}
              </span>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {isRename ? 'Save' : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
